import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Check, ExternalLink, Loader2, Sparkles, Undo2, X } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { cn } from '@orisini/ui/lib/utils'
import { accountsApi, type Workspace } from '../../accounts/lib/accounts'
import { ago, discoverApi, type DiscoverItem, type DiscoverStatus } from '../lib/discover'
import { contentApi, type Kind } from '../../content/lib/content'

const TAB_LABEL: Record<DiscoverStatus, string> = { new: 'New', selected: 'Selected', ignored: 'Ignored' }

/**
 * /discover — berita yang ditemukan Orisini untuk topik tiap akun.
 *
 * Di sinilah user memutuskan berita mana yang akan diproduksi: pilih, lalu
 * "Create content" membuat draft poster/carousel untuk akun berita itu.
 */
export default function DiscoverPage() {
  const [params, setParams] = useSearchParams()
  const account = params.get('account') ?? ''
  const status = (params.get('status') as DiscoverStatus) || 'new'
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [items, setItems] = useState<DiscoverItem[] | null>(null)
  const [counts, setCounts] = useState<Record<DiscoverStatus, number>>({ new: 0, selected: 0, ignored: 0 })
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [kinds, setKinds] = useState<Kind[]>(['poster'])
  const navigate = useNavigate()

  const produksi = async () => {
    if (!picked.size || !kinds.length) return
    setBusy(true)
    try {
      const r = await contentApi.produceFromDiscover([...picked], kinds)
      toast.success(`${r.created} ${r.created === 1 ? 'draft is' : 'drafts are'} being written`)
      navigate('/review')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't start production")
      setBusy(false)
    }
  }

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
  }

  const muat = useCallback(async () => {
    try {
      const r = await discoverApi.list(status, account || undefined)
      setItems(r.items)
      setCounts(r.counts)
      setPicked(new Set())
    } catch (err) {
      setItems([])
      toast.error(err instanceof Error ? err.message : "Couldn't load Discover")
    }
  }, [status, account])

  useEffect(() => {
    setItems(null)
    void muat()
  }, [muat])

  useEffect(() => {
    accountsApi.workspaces().then(setWorkspaces).catch(() => undefined)
  }, [])

  const ubah = async (ids: string[], to: DiscoverStatus) => {
    if (!ids.length) return
    setBusy(true)
    try {
      await discoverApi.setStatus(ids, to)
      toast.success(
        to === 'selected'
          ? `${ids.length} ${ids.length === 1 ? 'story' : 'stories'} selected`
          : to === 'ignored'
            ? `${ids.length} ignored`
            : `${ids.length} moved back to New`,
      )
      await muat()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setBusy(false)
    }
  }

  const toggle = (id: string) => {
    const next = new Set(picked)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setPicked(next)
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Discover</h1>
          <p className="text-sm text-muted-foreground">Stories Orisini found for your projects' topics, from the last 3 days.</p>
        </div>
        {workspaces.length > 1 && (
          <select
            aria-label="Filter by project"
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={account}
            onChange={(e) => setParam('account', e.target.value)}
          >
            <option value="">All projects</option>
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        )}
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 rounded-md bg-muted p-1">
          {(Object.keys(TAB_LABEL) as DiscoverStatus[]).map((s) => (
            <button
              key={s}
              onClick={() => setParam('status', s === 'new' ? '' : s)}
              className={cn('rounded px-3 py-1.5 text-sm', status === s ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground')}
            >
              {TAB_LABEL[s]} <span className="tabular-nums text-muted-foreground">{counts[s]}</span>
            </button>
          ))}
        </div>

        {picked.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{picked.size} chosen</span>
            <span className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs">
              {(['poster', 'carousel', 'video'] as Kind[]).map((k) => (
                <label key={k} className="flex items-center gap-1 px-1">
                  <input type="checkbox" checked={kinds.includes(k)} onChange={() => setKinds(kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k])} />
                  {k === 'poster' ? 'Poster' : k === 'carousel' ? 'Carousel' : 'Video'}
                </label>
              ))}
            </span>
            <Button size="sm" disabled={busy || !kinds.length} onClick={produksi}>
              <Sparkles /> Create content
            </Button>
            {status !== 'selected' && (
              <Button size="sm" disabled={busy} onClick={() => ubah([...picked], 'selected')}>
                <Check /> Select
              </Button>
            )}
            {status !== 'ignored' && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => ubah([...picked], 'ignored')}>
                <X /> Ignore
              </Button>
            )}
            {status !== 'new' && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => ubah([...picked], 'new')}>
                <Undo2 /> Back to New
              </Button>
            )}
          </div>
        )}
      </div>

      {status === 'selected' && counts.selected > 0 && (
        <p className="text-sm text-muted-foreground">
          Stories you turn into content are marked Selected. Choose some and press Create content to make more from them.
        </p>
      )}

      {items === null ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <EmptyState status={status} hasAccounts={workspaces.length > 0} />
      ) : (
        <ul className="space-y-3">
          {items.map((it) => (
            <li
              key={it.id}
              className={cn('flex gap-3 rounded-lg border p-4 transition-colors', picked.has(it.id) ? 'border-primary bg-primary/5' : 'border-border')}
            >
              <input
                type="checkbox"
                className="mt-1 size-4 shrink-0"
                checked={picked.has(it.id)}
                onChange={() => toggle(it.id)}
                aria-label={`Choose "${it.title}"`}
              />
              {it.imageUrl && (
                <img
                  src={it.imageUrl}
                  alt=""
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  className="hidden size-20 shrink-0 rounded-md bg-muted object-cover sm:block"
                  onError={(e) => (e.currentTarget.style.display = 'none')}
                />
              )}
              <div className="min-w-0 flex-1 space-y-1.5">
                <a href={it.url} target="_blank" rel="noopener noreferrer" className="group inline-flex items-start gap-1 font-medium hover:underline">
                  {it.title}
                  <ExternalLink className="mt-1 size-3.5 shrink-0 text-muted-foreground group-hover:text-foreground" />
                </a>
                <p className="text-xs text-muted-foreground">
                  {[it.sourceName, ago(it.publishedAt ?? it.foundAt), it.topicName, workspaces.length > 1 || !account ? it.workspaceName : null]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                {(it.summary || it.excerpt) && <p className="text-sm text-muted-foreground">{it.summary ?? it.excerpt}</p>}
                {it.angle && (
                  <p className="text-sm">
                    <span className="text-muted-foreground">Idea: </span>
                    {it.angle}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                <span
                  className="rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums"
                  title={it.usedAi ? 'Relevance scored by AI' : 'Matched by keywords — AI scoring was unavailable'}
                >
                  {it.usedAi ? `${it.score}%` : 'Keyword match'}
                </span>
                <div className="flex gap-1">
                  {it.status !== 'selected' && (
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => ubah([it.id], 'selected')} title="Select" aria-label="Select">
                      <Check />
                    </Button>
                  )}
                  {it.status !== 'ignored' && (
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => ubah([it.id], 'ignored')} title="Ignore" aria-label="Ignore">
                      <X />
                    </Button>
                  )}
                  {it.status !== 'new' && (
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => ubah([it.id], 'new')} title="Back to New" aria-label="Back to New">
                      <Undo2 />
                    </Button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function EmptyState({ status, hasAccounts }: { status: DiscoverStatus; hasAccounts: boolean }) {
  if (!hasAccounts) {
    return (
      <p className="text-sm text-muted-foreground">
        <Link to="/projects/new" className="text-foreground underline-offset-4 hover:underline">Create a project</Link> and give it
        a few topics — stories that fit them show up here.
      </p>
    )
  }
  if (status !== 'new') return <p className="text-sm text-muted-foreground">Nothing here.</p>
  return (
    <p className="text-sm text-muted-foreground">
      No new stories yet. Add topics to a project under{' '}
      <Link to="/projects" className="text-foreground underline-offset-4 hover:underline">Projects</Link> → Topics; Orisini checks every
      15 minutes.
    </p>
  )
}
